/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { colors } from "@farmlands/mui-theme";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Box,
  Divider,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  Link,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  styled,
  useTheme,
} from "@mui/material";
import { AxiosError } from "axios";
import dayjs from "dayjs";
import _ from "lodash";
import React, { useEffect } from "react";
import { FormProvider, Resolver, useForm, useFormContext, useWatch } from "react-hook-form";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { z } from "zod";

import Checkbox from "../../../../../components/Checkbox";
import Input, { InputProps } from "../../../../../components/Input";
import { Loader } from "../../../../../components/Loader";
import MaterialSymbol from "../../../../../components/MaterialSymbol";
import { OrderSummary } from "../../../../../components/OrderSummary";
import {
  BoldWrapper,
  OrderLocationButton,
  PadButton,
  PadContent,
  PadIcon,
  WrapperA,
} from "../../../../../components/Pad/Pad";
import TextField from "../../../../../components/TextField";
import Typography from "../../../../../components/Typography";
import { splitAddress } from "../../../../../services/als/splitAddress";
import TestIds from "../../../../../testIds";
import { addGST, getGST } from "../../../../../util/addGst";
import { formatPrice } from "../../../../../util/helpers";
import { log } from "../../../../../util/log";
import { nameRegex } from "../../../../../util/validateName";
import { nzPhoneNumberRegex } from "../../../../../util/validatePhoneNumber";
import {
  selectCurrentFarmerAccount,
  selectEmailForPrefill,
  selectNameForPrefill,
  selectPhoneForPrefill,
} from "../../../../azure-b2c/storage";
import { ErrorCode } from "../../../../drawdown/v2/DrawdownRequestModal/api";
import { AGREEMENT_CAMPAIGN_SUCCESS_ROUTE } from "../../../constants";
import { submitCampaignApi } from "../api";
import { ICampaignState, useCampaignContext } from "../CampaignContext";
import { RESTART_ERROR } from "../constants";
import { AccordionItemSummary, getTotalUnits, SalesAccordionItem } from "../QuantityTab";
import TabHeader from "../TabHeader";
import {
  AgreementForm,
  SaleAgreementPromo,
  SalesAgreementPromoProductSelectionVariant,
} from "../types";
import { calculateTotalFreightCost, getPrice } from "../utils";

import { ReviewTabFooter } from "./ReviewTabFooter";

const AGREEMENT_FORM_KEYS = {
  accName: "accName",
  accEmail: "accEmail",
  accPhoneNumber: "accPhoneNumber",
  productsSelected: "productsSelected",
  splitPayment: "splitPayment",
  reference: "reference",
  splitPaymentAccName: "splitPaymentAccName",
  splitPaymentAccNumber: "splitPaymentAccNumber",
  splitPaymentContribution: "splitPaymentContribution",
  tcAccepted: "tcAccepted",
  deliveryContactName: "deliveryContactName",
  deliveryPhoneNum: "deliveryPhoneNum",
  deliverySupplyNum: "deliverySupplyNum",
  deliveryDetails: "deliveryDetails",
  freightType: "freightType",
  hiabTruckRequired: "hiabTruckRequired",
  truck: "truck",
  carrier: "carrier",
} as const;

const calculateTotalPrice = (products: SalesAgreementPromoProductSelectionVariant[]) => {
  return products.reduce((total, product) => {
    if (!product.orders) {
      return 0;
    }
    const totalOrders = product.orders.reduce((sum, val) => sum + val.quantity, 0);
    return total + totalOrders * getPrice(product);
  }, 0);
};

const StyledSplitPaymentInnerContainer = styled(Stack)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: "1rem",
  [theme.breakpoints.up("md")]: {
    flexDirection: "row",
  },
}));

const StyledSplitPaymentContainer = styled(Stack, {
  shouldForwardProp: (prop) => prop !== "",
})<{ checked?: boolean }>(({ theme, checked }) => ({
  background: checked ? theme.colors.primary100 : "transparent",
  borderRadius: checked ? "8px" : "0",
  gap: "4px",
  padding: "8px",
}));

const StyledSummaryContainer = styled(Stack)(({ theme }) => ({
  background: theme.colors.primary50,
  border: `1px solid ${theme.colors.primary200}`,
}));

function hasAnyNonNullishField<T extends Record<string, unknown>>(obj: T): boolean {
  return Object.values(obj).some((value) => value != null);
}

export interface ReviewTabProps {
  config: SaleAgreementPromo;
  refreshCampaign: () => void;
}

export const ReviewTab: React.FC<ReviewTabProps> = ({ config, refreshCampaign }) => {
  const account = useSelector(selectCurrentFarmerAccount);
  const { state, setState, reset } = useCampaignContext();
  const isFreightInclusive = !config.freightRequired;
  const [submitting, setSubmitting] = React.useState(false);
  const [apiError, setApiError] = React.useState<string>();
  const navigate = useNavigate();

  const totalFreightCost = isFreightInclusive
    ? 0
    : calculateTotalFreightCost(state.productSelected, state.freightCharge);
  const totalPrice = calculateTotalPrice(state.productSelected);

  const accountEmail = useSelector(selectEmailForPrefill);
  const accountUsername = useSelector(selectNameForPrefill);
  const accountPhone = useSelector(selectPhoneForPrefill);

  const dairySupplyNum =
    state.addressSelected && "dairySupplyNumber" in state.addressSelected
      ? (state.addressSelected.dairySupplyNumber as string)
      : "";

  const initialValues: AgreementForm = React.useMemo(() => {
    return {
      accName: accountUsername ?? "",
      accEmail: accountEmail ?? "",
      accPhoneNumber: accountPhone ?? "",
      productsSelected: state.productSelected,
      splitPayment: false,
      reference: "",
      splitPaymentAccName: "",
      splitPaymentAccNumber: "",
      splitPaymentContribution: "0",
      tcAccepted: false,
      deliveryContactName: "",
      deliveryPhoneNum: "",
      deliverySupplyNum: dairySupplyNum,
      deliveryDetails: "",
      freightType: "",
      hiabTruckRequired: false,
      truck: "",
      carrier: "",
    };
  }, [accountUsername, accountEmail, accountPhone, state.productSelected, dairySupplyNum]);

  const hasFreightOptions = !!(
    state.deliveryOption &&
    state.deliveryOption.freightOptions &&
    state.deliveryOption.freightOptions.length >= 1
  );

  const hasTruckOptions = !!(
    state.deliveryOption?.trucks && state.deliveryOption.trucks.length >= 1
  );
  const isDeliveryMethod = state.deliveryOption?.type === "Delivery";

  const carrierOptions = (value: AgreementForm["freightType"]) =>
    state.deliveryOption?.freightOptions?.find((a) => a.type === value)?.carriers ?? [];

  const hasCarrierOptions = (value: AgreementForm["freightType"]) => {
    if (!value) {
      return false;
    }
    if (!carrierOptions(value)) {
      return false;
    }

    return carrierOptions(value)?.length && carrierOptions(value)?.length >= 1;
  };

  const freightSummary = React.useMemo(() => {
    if (isFreightInclusive) {
      return {
        deliveryCost: "",
        DeliveryLabel: <></>,
        FreightNoted: (
          <Typography variant="body2" fontWeight={600} textAlign="right">
            {config.freightNote}
          </Typography>
        ),
      };
    }

    return {
      deliveryCost: formatPrice(addGST(totalFreightCost)),
      DeliveryLabel: (
        <Typography variant="body2">
          Freight&nbsp;
          <Typography fontSize="12px" component="span" lineHeight={"18px"}>
            (GST incl.)
          </Typography>
        </Typography>
      ),
      FreightNoted: null,
    };
  }, [isFreightInclusive, config]);

  const checkTnC = config.termsConditionsTickRequired ? z.literal(true) : z.boolean().optional();
  const [overtime, setOvertime] = React.useState(false);

  useEffect(() => {
    // Check initially
    setOvertime(dayjs().diff(state.timer, "minutes") >= 60);

    // Set up interval to check every second
    const interval = setInterval(() => {
      setOvertime(dayjs().diff(state.timer, "minutes") >= 60);
    }, 1000);

    // Clean up interval on unmount
    return () => clearInterval(interval);
  }, [state.timer]);

  const zodValidationSchema = (values: AgreementForm) =>
    z
      .object({
        productsSelected: z.custom<SalesAgreementPromoProductSelectionVariant[]>(),
        accName: z.string().min(1, "Please enter a name."),
        accEmail: z.string().email("Invalid email format.").min(1, "Please enter an email."),
        accPhoneNumber: z
          .string()
          .trim()
          .regex(nzPhoneNumberRegex, "Invalid phone number format.")
          .min(1, "Please enter a phone number."),
        reference: z.string().max(60, "Maximum 60 characters allowed.").optional(),
        splitPayment: z.boolean(),
        splitPaymentAccName: z.string().optional(),
        splitPaymentAccNumber: z.string().optional(),
        splitPaymentContribution: z.string().optional(),
        tcAccepted: !account?.hasTfoAccess ? z.literal(true) : checkTnC,
        deliveryContactName: isDeliveryMethod
          ? z
              .string()
              .regex(nameRegex, "Invalid name format. (alphabets only)")
              .min(1, "Delivery contact name is required.")
          : z.string().optional(),
        deliveryPhoneNum: isDeliveryMethod
          ? z
              .string()
              .trim()
              .regex(nzPhoneNumberRegex, "Invalid phone number format.")
              .min(1, "Delivery phone number is required.")
          : z.string().optional(),
        deliverySupplyNum: z.string().optional(),
        deliveryDetails: z.string().optional(),
        hiabTruckRequired: z.boolean().optional(),
        freightType: hasFreightOptions
          ? z.string().min(1, "Please choose an option.")
          : z.string().optional(),
        truck: hasTruckOptions
          ? z.string().min(1, "Please choose a truck.")
          : z.string().optional(),
        carrier: hasCarrierOptions(values.freightType)
          ? z.string().min(1, "Please choose a carrier.")
          : z.string().optional(),
      })
      .superRefine((data, ctx) => {
        if (data.splitPayment) {
          if (!data.splitPaymentAccName) {
            ctx.addIssue({
              path: ["splitPaymentAccName"],
              message: "Required.",
              code: z.ZodIssueCode.custom,
            });
          }
          if (!data.splitPaymentAccNumber) {
            ctx.addIssue({
              path: ["splitPaymentAccNumber"],
              message: "Required.",
              code: z.ZodIssueCode.custom,
            });
          } else if (!/^\d*$/.test(data.splitPaymentAccNumber)) {
            ctx.addIssue({
              path: ["splitPaymentAccNumber"],
              message: "Number format only.",
              code: z.ZodIssueCode.custom,
            });
          } else if (data.splitPaymentAccNumber.length !== 9) {
            ctx.addIssue({
              path: ["splitPaymentAccNumber"],
              message: "Must be 9 digit number.",
              code: z.ZodIssueCode.custom,
            });
          }

          if (
            data.splitPaymentContribution === undefined ||
            Number(data.splitPaymentContribution) < 1
          ) {
            ctx.addIssue({
              path: ["splitPaymentContribution"],
              message: "Minimum 1% or more.",
              code: z.ZodIssueCode.custom,
            });
          }
        }
      });

  const resetForm = () => {
    reset();
    refreshCampaign();
    setState({ timer: Date.now(), tabIndex: 0 });
  };

  const form = useForm<AgreementForm>({
    defaultValues: initialValues,
    resolver: ((values: AgreementForm, context, options) =>
      zodResolver(zodValidationSchema(values))(
        values,
        context,
        options as never
      )) as Resolver<AgreementForm>,
  });

  const {
    handleSubmit,
    setValue,
    formState: { errors },
  } = form;

  const onSubmit = (value: AgreementForm) => {
    if (account) {
      setSubmitting(true);
      submitCampaignApi(value, state, account, config)
        .then(() => {
          navigate(AGREEMENT_CAMPAIGN_SUCCESS_ROUTE, {
            state: {
              campaignTitle: config?.displayTitle ?? config?.documentTitle,
            },
          });
        })
        .catch((error: AxiosError<{ type: ErrorCode }>) => {
          if (
            error.response?.data.type &&
            [
              ErrorCode.InvalidAgreement,
              ErrorCode.InvalidAgreementLine,
              ErrorCode.InvalidAgreementQuantity,
              ErrorCode.InvalidAgreementSchedule,
            ].includes(error.response?.data.type)
          ) {
            setApiError(RESTART_ERROR);
            refreshCampaign();
          } else {
            setApiError(
              "We’re sorry, but an unexpected error has occurred. We are unable to submit this agreement at this time."
            );
          }
          log(error);
        })
        .finally(() => {
          setSubmitting(false);
        });
    }
  };

  return (
    <FormProvider {...form}>
      <form key="campaign-submission" style={{ height: "100%" }} onSubmit={handleSubmit(onSubmit)}>
        <Stack height="100%" margin="auto" display={submitting ? "flex" : "none"}>
          <Loader title="Submitting" />
        </Stack>
        <Stack direction="column" gap="1rem" pb="50px" display={submitting ? "none" : undefined}>
          <TabHeader title="Summary" caption="Review your products and confirm agreement" />
          <ProductsSelected isFreightInclusive={isFreightInclusive} />
          <StyledSummaryContainer p="1rem" direction="column">
            <Stack direction={"column"} gap="1rem">
              <OrderSummary
                showDivider
                accountName={account?.accountName}
                accountNumber={account?.accountNumber}
                gst={formatPrice(getGST(totalPrice + totalFreightCost))}
                title="Order Summary"
                total={formatPrice(addGST(totalPrice + totalFreightCost))}
                delivery={freightSummary.deliveryCost}
                deliveryLabel={freightSummary.DeliveryLabel}
              />

              {isDeliveryMethod ? (
                <>
                  {freightSummary.FreightNoted}
                  <DeliveryAddressSelected />
                  <DeliveryInfoForm state={state} />
                </>
              ) : null}

              <AccountHolder />

              <Stack>
                <SplitPaymentOption />
                {!account?.hasTfoAccess ? (
                  <StyledSplitPaymentContainer>
                    <Checkbox
                      label={
                        <Typography variant="body2" fontWeight={400}>
                          I accepted the&nbsp;
                          <Link target="_blank" href={config.termsConditionsUrl}>
                            terms and conditions
                          </Link>
                          &nbsp;for these products
                        </Typography>
                      }
                      onChange={(_, checked) =>
                        setValue(AGREEMENT_FORM_KEYS.tcAccepted, checked, { shouldValidate: true })
                      }
                    />
                    {errors.tcAccepted && (
                      <Typography variant="body2" color="error500">
                        Terms and Conditions must be accepted
                      </Typography>
                    )}
                  </StyledSplitPaymentContainer>
                ) : config.termsConditionsTickRequired ? (
                  <StyledSplitPaymentContainer>
                    <Checkbox
                      label={
                        <Typography variant="body2" fontWeight={400}>
                          {config.termsConditionsText}
                        </Typography>
                      }
                      onChange={(_, checked) =>
                        setValue(AGREEMENT_FORM_KEYS.tcAccepted, checked, { shouldValidate: true })
                      }
                    />
                    {errors.tcAccepted && (
                      <Typography variant="body2" color="error500">
                        Terms and Conditions must be accepted
                      </Typography>
                    )}
                  </StyledSplitPaymentContainer>
                ) : (
                  <Typography variant="body2" color="grey800">
                    {config.termsConditionsText}
                  </Typography>
                )}
              </Stack>
            </Stack>
          </StyledSummaryContainer>
        </Stack>
        <ReviewTabFooter
          submitting={submitting}
          submitForm={() => handleSubmit(onSubmit)()}
          resetForm={resetForm}
          hasNonNullishField={hasAnyNonNullishField(errors)}
          errorMessage={apiError}
          overtime={overtime}
        />
      </form>
    </FormProvider>
  );
};

interface AgreementInputProps extends Pick<
  InputProps,
  | "label"
  | "testId"
  | "type"
  | "multiline"
  | "placeholder"
  | "required"
  | "helperText"
  | "helperTextColor"
  | "disableError"
  | "disabled"
  | "inputProps"
  | "minRows"
> {
  formKey: keyof AgreementForm;
}

const AgreementInput: React.FC<AgreementInputProps> = ({ formKey, ...restProps }) => {
  const {
    watch,
    setValue,
    formState: { errors },
  } = useFormContext<AgreementForm>();

  const theme = useTheme();
  const value = watch(formKey);
  if (typeof value !== "string") {
    return;
  }
  const error = errors[formKey]?.message as string | undefined;

  return (
    <Input
      onChange={(e) => {
        setValue(formKey, e.target.value, { shouldValidate: true });
      }}
      value={value}
      error={Boolean(error)}
      errorMessage={error}
      helperTextColor={theme.colors.grey700}
      {...restProps}
    />
  );
};

const AgreementSelect: React.FC<
  Omit<AgreementInputProps, "type"> & { options: { label: string; value: string }[] }
> = ({ options, formKey, testId, label, required, placeholder }) => {
  const {
    watch,
    setValue,
    formState: { errors },
  } = useFormContext<AgreementForm>();
  const theme = useTheme();
  const value = watch(formKey);
  const error = errors[formKey]?.message as string | undefined;

  return (
    <Box flexGrow={1} width={"100%"} display="flex" flexDirection="column">
      <Box display="flex" gap={1}>
        <InputLabel htmlFor={testId}>{label}</InputLabel>
        <InputLabel
          sx={{
            fontWeight: "400",
            color: !error ? colors.grey700 : colors.error700,
          }}
        >
          {required ? "(Required)" : ""}
        </InputLabel>
      </Box>
      <Select
        data-id={testId}
        value={value}
        onChange={(e) => setValue(formKey, e.target.value, { shouldValidate: true })}
        sx={{
          ".MuiSelect-icon": {
            top: "calc(50% - .5em) !important",
          },
        }}
      >
        <MenuItem value="" disabled>
          {placeholder}
        </MenuItem>
        {options.map((opt) => (
          <MenuItem key={opt.value} value={opt.value}>
            {opt.label}
          </MenuItem>
        ))}
      </Select>
      {error ? (
        <FormHelperText error sx={{ color: theme.colors.error700 }}>
          {error}
        </FormHelperText>
      ) : null}
    </Box>
  );
};

const AgreementRadio: React.FC<
  Omit<AgreementInputProps, "label" | "type"> & { options: { label: string; value: string }[] }
> = ({ options, formKey, testId }) => {
  const {
    watch,
    setValue,
    formState: { errors },
  } = useFormContext<AgreementForm>();
  const theme = useTheme();
  const value = watch(formKey);
  const error = errors[formKey]?.message as string | undefined;
  return (
    <Stack>
      <RadioGroup
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "1rem",
        }}
        data-id={testId}
        value={value}
        onChange={(e) => setValue(formKey, e.target.value, { shouldValidate: true })}
      >
        {options.map((opt) => (
          <FormControlLabel
            slotProps={{
              typography: {
                variant: "body2",
              },
            }}
            key={opt.value}
            label={opt.label}
            value={opt.value}
            control={<Radio />}
          />
        ))}
      </RadioGroup>
      {error ? (
        <FormHelperText error sx={{ color: theme.colors.error700 }}>
          {error}
        </FormHelperText>
      ) : null}
    </Stack>
  );
};

const AgreementInputPercent: React.FC<AgreementInputProps> = ({ formKey, type, testId, label }) => {
  const {
    watch,
    setValue,
    formState: { errors },
  } = useFormContext<AgreementForm>();
  const theme = useTheme();
  const value = watch(formKey);
  const error = errors[formKey]?.message as string | undefined;

  return (
    <TextField
      sx={{ padding: "10.5px 8px" }}
      label={label}
      type={type}
      onChange={(e) => {
        const val = e.target.value;

        if (RegExp(/\D/).exec(val)) {
          return e.preventDefault();
        }

        if (Number(val) > 100) {
          return;
        }

        setValue(formKey, _.trimStart(String(val), "0"), { shouldValidate: true });
      }}
      value={String(value)}
      variant={"outlined"}
      testId={testId}
      inputProps={{
        inputMode: "numeric",
      }}
      error={Boolean(error)}
      helperText={error ?? " "}
      helperTextColor={theme.colors.error700}
      InputProps={{
        endAdornment: <MaterialSymbol color={theme.colors.primary500}>percent</MaterialSymbol>,
      }}
    />
  );
};

interface ProductsSelectedProps {
  isFreightInclusive: boolean;
}

const ProductsSelected = ({ isFreightInclusive }: ProductsSelectedProps) => {
  const { state } = useCampaignContext();

  return state.productSelected.map((prod) => {
    const summary: AccordionItemSummary[] = [
      {
        type: "unit",
        title: prod.unit,
        calculation: prod.orders ? prod.orders.reduce((a, b) => a + b.quantity, 0) : 0,
      },
      {
        type: "price",
        title: "Total",
        calculation: getPrice(prod) * getTotalUnits(state.productSelected, prod.productId),
      },
    ];

    if (!isFreightInclusive) {
      summary.push({
        type: "price",
        title: "Freight",
        calculation: calculateTotalFreightCost(prod, state.freightCharge),
      });
    }

    return (
      <SalesAccordionItem
        key={prod.productId}
        productId={prod.productId}
        unit={prod.unit}
        itemNumber={prod.itemNumber}
        minQuantity={prod.quantity}
        title={prod.description}
        deliveryMonthPlans={prod.deliveryMonthPlans}
        selectedDeliveryMonthPlan={prod.selectedDeliveryMonthPlan}
        editable={false}
        defaultExpanded={true}
        summary={summary}
      />
    );
  });
};

const DeliveryAddressSelected = () => {
  const { state, setState } = useCampaignContext();
  const handleOpenPickUpStoreModal = () => {
    setState({ tabIndex: 0 });
  };

  if (state.addressSelected) {
    const address = state.addressSelected;
    let subtitle = "";
    if ("UniqueId" in address) {
      const { first, second } = splitAddress(address.FullAddress);
      subtitle = `${first}, ${second}`;
    } else {
      subtitle = `${address.street.replace("\n", ", ")} ${address.city}, ${address.zipCode}`;
    }
    return (
      <OrderLocationButton
        shade="light"
        onClick={handleOpenPickUpStoreModal}
        buttonTestId={TestIds.feat.campaign.reviewTab.orderLocationButton}
        title="Delivery"
        subtitle={
          <>
            {subtitle.split("\n").map((e) => {
              return (
                <span key={e}>
                  {e}
                  <br />
                </span>
              );
            })}
          </>
        }
        isEditEnabled
      />
    );
  }
  return (
    <PadButton
      shade="light"
      sx={{ justifyContent: "flex-start", p: 1 }}
      onClick={handleOpenPickUpStoreModal}
      data-testid={TestIds.feat.campaign.reviewTab.pickupOrDelivery}
    >
      <WrapperA>
        <BoldWrapper>
          <PadIcon className="material-symbols-outlined">add</PadIcon>
          <PadContent>Select pickup store or delivery</PadContent>
        </BoldWrapper>
      </WrapperA>
    </PadButton>
  );
};

const DeliveryInfoForm = ({ state }: { state: ICampaignState }) => {
  const { setValue } = useFormContext<AgreementForm>();
  const freightType = useWatch<AgreementForm, typeof AGREEMENT_FORM_KEYS.freightType>({
    name: AGREEMENT_FORM_KEYS.freightType,
  });

  const carrierOptions = state.deliveryOption?.freightOptions
    ?.find((a) => a.type === freightType)
    ?.carriers?.map((c) => ({
      label: c,
      value: c,
    }));

  useEffect(() => {
    setValue(AGREEMENT_FORM_KEYS.carrier, "", { shouldValidate: false });
  }, [freightType, setValue]);

  return (
    <Stack gap={1}>
      <AgreementInput
        label="Delivery contact name"
        formKey={AGREEMENT_FORM_KEYS.deliveryContactName}
        placeholder="Enter a delivery contact name here..."
        testId={TestIds.feat.campaign.reviewTab.deliveryInfo.contactNameInput}
        required
        type="text"
      />

      <AgreementInput
        label="Delivery phone number"
        formKey={AGREEMENT_FORM_KEYS.deliveryPhoneNum}
        placeholder="Enter a delivery phone number..."
        testId={TestIds.feat.campaign.reviewTab.deliveryInfo.phoneNumberInput}
        required
        type="tel"
      />

      <AgreementInput
        type="tel"
        label="Dairy supply number"
        formKey={AGREEMENT_FORM_KEYS.deliverySupplyNum}
        placeholder="Include your Dairy supply number, if you have one."
        inputProps={{ maxLength: 60 }}
        testId={TestIds.feat.campaign.reviewTab.deliveryInfo.supplyNumberInput}
        // helperText=''
        // helperTextColor={colors.grey700}
      />

      <AgreementInput
        type="text"
        multiline
        minRows={4}
        placeholder="Enter additional delivery details here..."
        formKey={AGREEMENT_FORM_KEYS.deliveryDetails}
        label="Delivery Details"
        testId={TestIds.feat.campaign.reviewTab.deliveryInfo.detailsInput}
      />
      {(state.deliveryOption &&
        state.deliveryOption.freightOptions &&
        state.deliveryOption.freightOptions.length >= 1) ||
      (state.deliveryOption?.trucks && state.deliveryOption.trucks.length >= 1) ||
      carrierOptions ? (
        <Divider sx={{ mb: "24px" }} />
      ) : null}
      {state.deliveryOption &&
      state.deliveryOption.freightOptions &&
      state.deliveryOption.freightOptions.length >= 1 ? (
        <AgreementRadio
          options={state.deliveryOption.freightOptions.map((fo) => ({
            label: fo.text,
            value: fo.type,
          }))}
          formKey={AGREEMENT_FORM_KEYS.freightType}
          testId={TestIds.feat.campaign.reviewTab.deliveryInfo.radioGroup}
        />
      ) : null}

      {(state.deliveryOption?.trucks && state.deliveryOption.trucks.length >= 1) ||
      (carrierOptions && carrierOptions.length >= 1) ? (
        <Typography variant="body2" my={2}>
          Please select your preferred carrier, if any
        </Typography>
      ) : null}
      {state.deliveryOption?.trucks && state.deliveryOption.trucks.length >= 1 ? (
        <AgreementSelect
          label="Truck type"
          required
          options={state.deliveryOption.trucks.map((val) => ({
            label: val,
            value: val,
          }))}
          testId={TestIds.feat.campaign.reviewTab.deliveryInfo.selectTruck}
          formKey={AGREEMENT_FORM_KEYS.truck}
          placeholder="Please choose a truck..."
        />
      ) : null}
      {carrierOptions && carrierOptions.length >= 1 ? (
        <AgreementSelect
          label="Carrier"
          options={carrierOptions}
          testId={TestIds.feat.campaign.reviewTab.deliveryInfo.selectCarrier}
          formKey={AGREEMENT_FORM_KEYS.carrier}
          required
          placeholder="Please choose a carrier..."
        />
      ) : null}
    </Stack>
  );
};

const AccountHolder = () => {
  const account = useSelector(selectCurrentFarmerAccount);
  const accountEmail = useSelector(selectEmailForPrefill);
  const accountUsername = useSelector(selectNameForPrefill);
  const { watch } = useFormContext<AgreementForm>();
  const reference = watch(AGREEMENT_FORM_KEYS.reference);

  const confirmTitle = "Account holder / authoriser";
  const nameLabel = account?.hasTfoAccess ? "Customer name" : "Name";
  const phoneLabel = account?.hasTfoAccess ? "Customer phone number" : "Phone number";
  const emailLabel = account?.hasTfoAccess ? "Customer email address" : "Email address";
  const refLabel = account?.hasTfoAccess ? "Customer reference" : "Your reference";

  return (
    <Stack direction={"column"} py="0.5rem" gap={2}>
      <Typography variant="h6" fontWeight={700} fontSize={"20px"}>
        {confirmTitle}
      </Typography>
      <Typography variant="body2">Please confirm your contact details</Typography>

      {account?.hasTfoAccess && (
        <Stack width="auto" borderLeft={`4px solid ${colors.grey900}`} pl="8px" gap="2px">
          <Typography variant="body2" fontWeight={"bold"}>
            {account?.accountName}
          </Typography>
          <Typography variant="body2">{account.accountNumber}</Typography>
        </Stack>
      )}
      <Stack>
        {!account?.hasTfoAccess && accountUsername !== undefined ? (
          <Stack gap={0.2} mb={2} borderLeft={`4px solid ${colors.grey900}`} pl={2}>
            <Typography variant="body2" fontWeight={"bold"}>
              {account?.accountName}
            </Typography>
            <Typography variant="body2">{account?.accountNumber}</Typography>
            <Typography variant="body2">{accountUsername}</Typography>
            <Typography variant="body2">{accountEmail}</Typography>
          </Stack>
        ) : (
          <>
            <AgreementInput
              type="text"
              label={nameLabel}
              testId={TestIds.feat.campaign.reviewTab.nameInput}
              required
              formKey={AGREEMENT_FORM_KEYS.accName}
            />
            <AgreementInput
              type="text"
              label={emailLabel}
              testId={TestIds.feat.campaign.reviewTab.emailInput}
              required
              formKey={AGREEMENT_FORM_KEYS.accEmail}
            />
          </>
        )}

        <AgreementInput
          type="tel"
          label={phoneLabel}
          testId={TestIds.feat.campaign.reviewTab.phoneInput}
          required
          formKey={AGREEMENT_FORM_KEYS.accPhoneNumber}
        />
        <AgreementInput
          type="text"
          label={refLabel}
          placeholder="e.g. Your Purchase Order reference if required"
          testId={TestIds.feat.campaign.reviewTab.refInput}
          formKey={AGREEMENT_FORM_KEYS.reference}
          helperText={`${reference.length}/60 characters`}
          inputProps={{ maxLength: 60 }}
          disableError
        />
      </Stack>
    </Stack>
  );
};

const SplitPaymentOption = () => {
  const { setValue } = useFormContext<AgreementForm>();
  const splitPayment = useWatch<AgreementForm, typeof AGREEMENT_FORM_KEYS.splitPayment>({
    name: AGREEMENT_FORM_KEYS.splitPayment,
  });
  const splitPaymentContribution = useWatch<
    AgreementForm,
    typeof AGREEMENT_FORM_KEYS.splitPaymentContribution
  >({
    name: AGREEMENT_FORM_KEYS.splitPaymentContribution,
  });

  return (
    <StyledSplitPaymentContainer checked={splitPayment}>
      <Checkbox
        label={
          <Typography variant="body2" fontWeight={400}>
            Split payment with another farm
          </Typography>
        }
        onChange={(_, checked) =>
          setValue(AGREEMENT_FORM_KEYS.splitPayment, checked, { shouldValidate: true })
        }
      />
      {splitPayment && (
        <StyledSplitPaymentInnerContainer>
          <AgreementInput
            type="text"
            formKey={AGREEMENT_FORM_KEYS.splitPaymentAccName}
            label="Account name"
            placeholder=""
            testId={TestIds.feat.campaign.reviewTab.splitPay.accNameInput}
          />
          <AgreementInput
            type="text"
            formKey={AGREEMENT_FORM_KEYS.splitPaymentAccNumber}
            label="Account number"
            placeholder=""
            testId={TestIds.feat.campaign.reviewTab.splitPay.accNumInput}
            inputProps={{ maxLength: 9 }}
          />
          <Stack gap="24px" direction={"row"} alignItems={"flex-start"}>
            <Stack width="30%" minWidth="140px">
              <AgreementInputPercent
                formKey={AGREEMENT_FORM_KEYS.splitPaymentContribution}
                label="Their contribution"
                placeholder=""
                testId={TestIds.feat.campaign.reviewTab.splitPay.splitInput}
                type="number"
              />
            </Stack>
            <Stack alignSelf="center">
              <Typography>-</Typography>
            </Stack>
            <Stack>
              <InputLabel>My contribution</InputLabel>
              <Stack direction="row" height="48px">
                <Typography
                  alignSelf={"center"}
                  data-testid={TestIds.feat.campaign.reviewTab.splitPay.splitResult}
                >
                  {`${100 - Number(splitPaymentContribution)}%`}
                </Typography>
              </Stack>
            </Stack>
          </Stack>
        </StyledSplitPaymentInnerContainer>
      )}
    </StyledSplitPaymentContainer>
  );
};
